import type { ReactNode } from "react";
import type { PublicProfileDraft } from "../../lib/public-profile-store";
import { Switch } from "../premium/switch";
import { uiLibrarySourceAttrs } from "../../lib/ui-library/ui-library-source-attrs";
import { ProfileConnectionMark } from "./ProfileConnectionMark";
import {
  connectionFromLegacySocial,
  LEGACY_SOCIAL_PROVIDERS,
  publicConnectionsOnly,
  type PublicConnection,
  type PublicConnectionProviderId,
} from "./profile-connections";

export interface ProfileCompositionProps {
  profile: PublicProfileDraft;
  mode: "editable" | "view";
  onChange?: (patch: Partial<PublicProfileDraft>) => void;
  onAvatarActivate?: () => void;
  onVisibilityChange?: (
    connections: PublicConnection[],
    providerId: PublicConnectionProviderId,
  ) => void;
  visibilityBusy?: PublicConnectionProviderId | null;
  actions?: ReactNode;
  compact?: boolean;
}

export function ProfileComposition({
  profile,
  mode,
  onChange,
  onAvatarActivate,
  onVisibilityChange,
  visibilityBusy,
  actions,
  compact = false,
}: ProfileCompositionProps) {
  const editable = mode === "editable";
  const visibleConnections = publicConnectionsOnly(profile.publicConnections);

  const updateSocial = (
    providerId: (typeof LEGACY_SOCIAL_PROVIDERS)[number]["id"],
    value: string,
  ) => {
    if (!onChange) return;
    const socialLinks = { ...profile.socialLinks, [providerId]: value };
    const existing = profile.publicConnections.find(
      (connection) => connection.providerId === providerId,
    );
    const replacement = connectionFromLegacySocial(
      providerId,
      value,
      existing?.isPublic === true,
    );
    const publicConnections = profile.publicConnections.filter(
      (connection) => connection.providerId !== providerId,
    );
    if (replacement) publicConnections.push(replacement);
    onChange({ socialLinks, publicConnections });
  };

  const toggleVisibility = (
    providerId: (typeof LEGACY_SOCIAL_PROVIDERS)[number]["id"],
    isPublic: boolean,
  ) => {
    const replacement = connectionFromLegacySocial(
      providerId,
      profile.socialLinks[providerId],
      isPublic,
    );
    if (!replacement || !onChange) return;
    const publicConnections = profile.publicConnections.filter(
      (connection) => connection.providerId !== providerId,
    );
    publicConnections.push(replacement);
    onChange({ publicConnections });
    onVisibilityChange?.(publicConnections, providerId);
  };

  return (
    <section
      {...uiLibrarySourceAttrs("beui-pro")}
      data-profile-mode={mode}
      className="profile-composition text-[var(--fintheon-text)]"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={editable ? onAvatarActivate : undefined}
            disabled={!editable || !onAvatarActivate}
            aria-label={editable ? "Update profile picture" : undefined}
            className="shrink-0 rounded-full disabled:cursor-default focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55"
          >
            {profile.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt=""
                className="h-14 w-14 rounded-full object-cover"
              />
            ) : (
              <span
                className="profile-avatar-placeholder settings-placeholder h-14 w-14"
                data-avatar-color={profile.avatarColor}
              />
            )}
          </button>
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-semibold">
              {profile.displayName || "Fintheon user"}
            </h3>
            <p className="truncate text-xs text-[var(--fintheon-text)]/58">
              {profile.position || "Desk team"}
            </p>
            {profile.broker ? (
              <p className="truncate text-[11px] text-[var(--fintheon-text)]/42">
                {profile.broker}
              </p>
            ) : null}
          </div>
        </div>
        {actions}
      </div>

      {editable ? (
        <div className="mt-5 space-y-4">
          <ProfileTextField
            label="Display name"
            value={profile.displayName}
            maxLength={32}
            onChange={(displayName) => onChange?.({ displayName })}
          />
          <label className="block">
            <span className="mb-1.5 block text-xs text-[var(--fintheon-text)]/56">
              Short bio
            </span>
            <textarea
              value={profile.bio}
              maxLength={180}
              rows={3}
              onChange={(event) => onChange?.({ bio: event.target.value })}
              placeholder="One useful line for the floor"
              className="min-h-20 w-full resize-y rounded-lg bg-[var(--fintheon-surface)] px-3 py-2 text-sm leading-5 outline-none transition-colors duration-200 placeholder:text-[var(--fintheon-text)]/28 focus:bg-[#110f0a]"
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <ProfileTextField
              label="Desk position"
              value={profile.position}
              maxLength={48}
              placeholder="Lead trader, risk, analyst"
              onChange={(position) => onChange?.({ position })}
            />
            <ProfileTextField
              label="Broker or prop firm"
              value={profile.broker}
              maxLength={48}
              placeholder="Topstep, Tradovate, IBKR"
              onChange={(broker) => onChange?.({ broker })}
            />
          </div>
        </div>
      ) : profile.bio && !compact ? (
        <p className="mt-4 text-sm leading-6 text-[var(--fintheon-text)]/72">
          {profile.bio}
        </p>
      ) : null}

      <div className="mt-5">
        <h4 className="text-xs font-medium text-[var(--fintheon-text)]/58">
          Public connections
        </h4>
        {editable ? (
          <div className="mt-2 space-y-2">
            {LEGACY_SOCIAL_PROVIDERS.map((provider) => {
              const value = profile.socialLinks[provider.id] ?? "";
              const connection = profile.publicConnections.find(
                (item) => item.providerId === provider.id,
              );
              const valid = Boolean(
                connectionFromLegacySocial(provider.id, value, true),
              );
              return (
                <div
                  key={provider.id}
                  className="flex min-h-11 items-center gap-3 rounded-lg bg-[var(--fintheon-surface)] px-3 py-2"
                >
                  <ProfileConnectionMark providerId={provider.id} />
                  <label className="min-w-0 flex-1">
                    <span className="sr-only">{provider.label}</span>
                    <input
                      value={value}
                      onChange={(event) =>
                        updateSocial(
                          provider.id,
                          event.target.value.slice(0, 120),
                        )
                      }
                      placeholder={provider.fieldHint}
                      className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--fintheon-text)]/28"
                    />
                  </label>
                  <span
                    className="profile-visibility-switch"
                    {...uiLibrarySourceAttrs("beui-pro")}
                  >
                    <Switch
                      checked={connection?.isPublic === true}
                      onCheckedChange={(checked) =>
                        toggleVisibility(provider.id, checked)
                      }
                      disabled={!valid || visibilityBusy === provider.id}
                      size="sm"
                      label="Public"
                    />
                  </span>
                </div>
              );
            })}
          </div>
        ) : visibleConnections.length ? (
          <div className="mt-2 space-y-1">
            {visibleConnections.map((connection) => (
              <a
                key={connection.id}
                href={connection.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-10 items-center gap-3 rounded-lg px-2 text-sm transition-colors duration-200 hover:bg-[var(--fintheon-surface)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55"
              >
                <ProfileConnectionMark providerId={connection.providerId} />
                <span className="font-medium">{connection.label}</span>
                <span className="ml-auto max-w-[58%] truncate text-xs text-[var(--fintheon-text)]/48">
                  {connection.handle || new URL(connection.url).hostname}
                </span>
              </a>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-xs text-[var(--fintheon-text)]/42">
            No public connections.
          </p>
        )}
      </div>
    </section>
  );
}

function ProfileTextField({
  label,
  value,
  maxLength,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  maxLength: number;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs text-[var(--fintheon-text)]/56">
        {label}
      </span>
      <input
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg bg-[var(--fintheon-surface)] px-3 py-2 text-sm outline-none transition-colors duration-200 placeholder:text-[var(--fintheon-text)]/28 focus:bg-[#110f0a]"
      />
    </label>
  );
}
